import { appConfig } from "@/commons";
import { RESOURCES_USER, ResourceUserType } from "@/constants/resources";
import {
  clearCurrentUser,
  clearLegacyAuthStorage,
  getCurrentUser,
} from "@/services/auth/session";
import type { AuthProvider } from "react-admin";

const PUBLIC_ROUTES = new Set([
  "/login",
  "/register",
  "/verify-email",
  "/forgot-password",
  "/reset-password",
]);

const isPublicRoute = () => {
  const rawPath = window.location.hash.startsWith("#/")
    ? window.location.hash.slice(1)
    : window.location.pathname;

  const pathname = rawPath.split("?")[0].replace(/\/+$/, "") || "/";

  return PUBLIC_ROUTES.has(pathname);
};

type ForgotPasswordParams = {
  email: string;
};
export interface GameooAuthProvider extends AuthProvider {
  forgotPassword: (params: ForgotPasswordParams) => Promise<void>;
}

const authProvider: GameooAuthProvider = {
  login: async ({ username, password }) => {
    clearCurrentUser();
    clearLegacyAuthStorage();

    const response = await fetch(`${appConfig.api.baseUrl}/auth/login`, {
      method: "POST",
      body: JSON.stringify({ pseudo: username, password }),
      headers: new Headers({ "Content-Type": "application/json" }),
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error("Invalid username or password");
    }

    // Validate that the backend cookie was set and populate the in-memory cache.
    await getCurrentUser({ force: true });
  },

  logout: async () => {
    try {
      const response = await fetch(`${appConfig.api.baseUrl}/auth/logout`, {
        method: "POST",
        credentials: "include",
      });

      if (!response.ok && response.status !== 401 && response.status !== 403) {
        throw new Error("Unable to log out");
      }
    } finally {
      clearCurrentUser();
      clearLegacyAuthStorage();
    }

    return "/login";
  },

  checkError: (error) => {
    if (error.status === 401 || error.status === 403) {
      clearCurrentUser();
      clearLegacyAuthStorage();
      return Promise.reject({ redirectTo: "/login", message: false });
    }

    return Promise.resolve();
  },

  checkAuth: async () => {
    console.trace("checkAuth");
    if (isPublicRoute()) {
      return;
    }

    await getCurrentUser();
  },

  canAccess: async ({ resource }) => {
    const user = await getCurrentUser();

    if (user.role === "user") {
      return RESOURCES_USER.includes(resource as ResourceUserType);
    }

    return false;
  },

  getPermissions: async () => {
    console.log("getPermissions", isPublicRoute());
    if (isPublicRoute()) {
      return null;
    }

    const user = await getCurrentUser();
    return user.role;
  },

  getIdentity: async () => {
    return getCurrentUser();
  },

  forgotPassword: async ({ email }) => {
    const response = await fetch(
      `${appConfig.api.baseUrl}/auth/forgot-password`,
      {
        method: "POST",
        headers: new Headers({
          Accept: "application/json",
          "Content-Type": "application/json",
        }),
        credentials: "include",
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
        }),
      },
    );

    if (!response.ok) {
      const data = await response.json().catch(() => null);

      const message = Array.isArray(data?.message)
        ? data.message.join(", ")
        : (data?.message ?? "Impossible d'envoyer la demande");

      throw new Error(message);
    }
  },
};

export { authProvider };
